import React, { useState, useEffect } from 'react';
import { keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import GavelIcon from '@mui/icons-material/Gavel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import request from '../api';
import { sanitizeIntegerInput } from '../numberUtils';

const BID_INCREMENTS = [1, 10, 50, 100];

// Shared with any screen that shows a live bid feed (ProductDetail, the live-room view)
// so a new bid gets the same slide-in/glow/amount-pop treatment everywhere.
export const HIGHLIGHT_DURATION_MS = 2200;

export const bidSlideIn = keyframes`
    0% { opacity: 0; transform: translateY(-18px) scaleY(0.97); }
    55% { opacity: 1; transform: translateY(2px) scaleY(1.01); }
    100% { opacity: 1; transform: translateY(0) scaleY(1); }
`;

export const bidGlowPulse = keyframes`
    0% { background-color: rgba(0, 194, 255, 0.22); box-shadow: inset 0 0 0 1px rgba(0, 194, 255, 0.45); }
    70% { background-color: rgba(0, 194, 255, 0.1); box-shadow: inset 0 0 0 1px rgba(0, 194, 255, 0.2); }
    100% { background-color: transparent; box-shadow: inset 0 0 0 1px rgba(0, 194, 255, 0); }
`;

export const amountPop = keyframes`
    0% { transform: scale(1); }
    35% { transform: scale(1.12); text-shadow: 0 0 16px rgba(0, 194, 255, 0.65); }
    100% { transform: scale(1); text-shadow: 0 0 0 rgba(0, 194, 255, 0); }
`;

export const badgeFade = keyframes`
    0% { opacity: 0; transform: translateX(-6px) scale(0.9); }
    15% { opacity: 1; transform: translateX(0) scale(1); }
    75% { opacity: 1; }
    100% { opacity: 0; }
`;

export function BidForm({ product, token, onBid }) {
    const minBid = product.current_bid != null ? Number(product.current_bid) : Number(product.starting_price);
    const minValidBid = Math.floor(minBid) + 1;
    const [amount, setAmount] = useState(String(minValidBid));
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setAmount(String(minValidBid));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minBid]);

    const addToAmount = (increment) => {
        setAmount((prev) => String((Number(prev) || 0) + increment));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            await request(`/products/${product.id}/bid`, {
                method: 'POST',
                token,
                body: { amount: Number(amount) },
            });
            onBid();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box component="form" onSubmit={submit} sx={{ mt: 2 }}>
            <TextField
                label="Tu puja (MXN)"
                type="number"
                size="small"
                inputProps={{ step: '1', min: minValidBid, inputMode: 'numeric' }}
                value={amount}
                onChange={(e) => setAmount(sanitizeIntegerInput(e.target.value))}
                fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                {BID_INCREMENTS.map((increment) => (
                    <Button
                        key={increment}
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => addToAmount(increment)}
                        sx={{ flex: 1 }}
                    >
                        +{increment}
                    </Button>
                ))}
            </Stack>
            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                startIcon={<GavelIcon />}
                fullWidth
                sx={{ mt: 1.5 }}
            >
                Pujar
            </Button>
            {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </Box>
    );
}

export function formatRemaining(ms) {
    const totalSeconds = Math.max(Math.floor(ms / 1000), 0);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

export function AuctionClock({ startTime, endTime }) {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!endTime) return undefined;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [endTime]);

    if (!endTime) {
        return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <AccessTimeIcon fontSize="small" color="disabled" />
                <Typography variant="body2" color="text.secondary">Esta subasta no tiene límite de tiempo.</Typography>
            </Stack>
        );
    }

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const total = Math.max(end - start, 1);
    const elapsed = Math.min(Math.max(now - start, 0), total);
    const progress = Math.min((elapsed / total) * 100, 100);
    const remainingMs = end - now;
    const ended = remainingMs <= 0;

    return (
        <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeIcon fontSize="small" color={ended ? 'disabled' : 'warning'} />
                    <Typography variant="body2" fontWeight={600}>
                        {ended ? 'Subasta finalizada' : `Quedan ${formatRemaining(remainingMs)}`}
                    </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {Math.round(progress)}%
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={progress}
                color={ended ? 'inherit' : 'warning'}
                sx={{ height: 8, borderRadius: 999 }}
            />
        </Box>
    );
}
